import React, { Component } from 'react';
import { array, func, number, object, oneOf, shape, string } from 'prop-types';
import { compose } from 'redux';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { withViewport } from '../../util/contextHelpers';
import { propTypes } from '../../util/types';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
} from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';
import { NamedRedirect } from '../../components';
import { editorFlags, visibleEditorGroups, visibleEditorSteps } from '../../config/features';

import Tabs from './EditListingWizardTabs/EditListingWizardTabs';
import EditListingWizardTab, { BASIC, GALLERY, TEAM, TOURING_LINKS } from './EditListingWizardTab';
import css from './EditListingWizard.module.css';

// You can reorder these panels.
// Note 1: You need to change save button translations for new listing flow
// Note 2: Ensure that draft listing is created after the first panel
// and listing publishing happens after last panel.
// Note 3: in FTW-hourly template we don't use the POLICY tab so it's commented out.
// If you want to add a free text field to your listings you can enable the POLICY tab
// Note 4: a step shows only when its flag in src/config/features.js (editorFlags.productionPage)
// is on.
export const TABS = visibleEditorSteps(editorFlags.productionPage, [
  BASIC,
  GALLERY,
  TEAM,
  TOURING_LINKS,
]);

// Tab groups for the left nav section headers
export const TAB_GROUPS = visibleEditorGroups(
  [
    {
      labelKey: 'EditListingWizard.groupProduction',
      tabs: [BASIC, GALLERY, TEAM],
    },
    {
      labelKey: 'EditListingWizard.groupDistribution',
      tabs: [TOURING_LINKS],
    },
  ],
  TABS
);

// Tabs are horizontal in small screens
const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

const tabLabel = (intl, tab) => {
  let key = null;
  if (tab === BASIC) {
    key = 'EditListingWizard.tabLabelBasicInfo';
  } else if (tab === GALLERY) {
    key = 'EditListingWizard.tabLabelGallery';
  } else if (tab === TEAM) {
    key = 'EditListingWizard.tabLabelTeam';
  } else if (tab === TOURING_LINKS) {
    key = 'EditListingWizard.tabLabelTouringLinks';
  }

  return intl.formatMessage({ id: key });
};

/**
 * Check if a wizard tab is completed.
 *
 * @param tab wizard's tab
 * @param listing is contains some specific data if tab is completed
 *
 * @return true if tab / step is completed.
 */
const tabCompleted = (tab, listing) => {
  const { price, description, title, publicData } = listing.attributes;
  const images = listing.images;
  const category = publicData?.category;

  switch (tab) {
    case BASIC:
      return !!(description && title && category);
    case GALLERY:
      return images && images.length > 0;
    case TEAM:
      return publicData?.completedTeamTab;
    case TOURING_LINKS:
      return !!price;
    default:
      return false;
  }
};

/**
 * Check which wizard tabs are active and which are not yet available. Tab is active if previous
 * tab is completed. In edit mode all tabs are active.
 *
 * @param isNew flag if a new listing is being created or an old one being edited
 * @param listing data to be checked
 *
 * @return object containing activity / editability of different tabs of this wizard
 */
const tabsActive = (isNew, listing) => {
  return TABS.reduce((acc, tab) => {
    const previousTabIndex = TABS.findIndex(t => t === tab) - 1;
    const isActive =
      previousTabIndex >= 0 ? !isNew || tabCompleted(TABS[previousTabIndex], listing) : true;
    return { ...acc, [tab]: isActive };
  }, {});
};

const scrollToTab = (tabPrefix, tabId) => {
  const el = document.querySelector(`#${tabPrefix}_${tabId}`);
  if (el) {
    el.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }
};

// Create a new or edit listing through EditListingWizard
class EditListingWizard extends Component {
  constructor(props) {
    super(props);

    // Having this info in state would trigger unnecessary rerendering
    this.hasScrolledToTab = false;

    this.state = {
      draftId: null,
    };
    this.handleCreateFlowTabScrolling = this.handleCreateFlowTabScrolling.bind(this);
    this.handlePublishListing = this.handlePublishListing.bind(this);
  }

  handleCreateFlowTabScrolling(shouldScroll) {
    this.hasScrolledToTab = shouldScroll;
  }

  handlePublishListing(id) {
    const { onPublishListingDraft } = this.props;
    onPublishListingDraft(id);
  }

  render() {
    const {
      id,
      className,
      rootClassName,
      params,
      listing,
      viewport,
      intl,
      errors,
      onManageDisableScrolling,
      currentUser,
      currentUserHasListings,
      orgIdFromUrl,
      ...rest
    } = this.props;

    const selectedTab = params.tab;
    const isNewListingFlow = [LISTING_PAGE_PARAM_TYPE_NEW, LISTING_PAGE_PARAM_TYPE_DRAFT].includes(
      params.type
    );
    const rootClasses = rootClassName || css.root;
    const classes = classNames(rootClasses, className);
    const currentListing = ensureListing(listing);
    const tabsStatus = tabsActive(isNewListingFlow, currentListing);

    const tabsTitleMessage = intl.formatMessage({ id: 'EditListingWizard.tabsTitle' });

    // If selectedTab is not active, redirect to the beginning of wizard.
    // A step that is switched off (or unknown) goes to the first step.
    if (!tabsStatus[selectedTab]) {
      const currentTabIndex = TABS.indexOf(selectedTab);
      const nearestActiveTab =
        currentTabIndex < 0
          ? TABS[0]
          : TABS.slice(0, currentTabIndex)
              .reverse()
              .find(t => tabsStatus[t]);

      return <NamedRedirect name="EditListingPage" params={{ ...params, tab: nearestActiveTab }} />;
    }

    const { width } = viewport;
    const hasViewport = width > 0;
    const hasHorizontalTabLayout = hasViewport && width <= MAX_HORIZONTAL_NAV_SCREEN_WIDTH;
    const hasVerticalTabLayout = hasViewport && width > MAX_HORIZONTAL_NAV_SCREEN_WIDTH;
    const hasFontsLoaded =
      hasViewport && document.documentElement.classList.contains('fontsLoaded');

    // Check if scrollToTab call is needed (tab is not visible on mobile)
    if (hasVerticalTabLayout) {
      this.hasScrolledToTab = true;
    } else if (hasHorizontalTabLayout && !this.hasScrolledToTab && hasFontsLoaded) {
      const tabPrefix = id;
      scrollToTab(tabPrefix, selectedTab);
      this.hasScrolledToTab = true;
    }

    const tabLink = tab => {
      return { name: 'EditListingPage', params: { ...params, tab } };
    };

    const setPortalRootAfterInitialRender = () => {
      if (!this.state.portalRoot) {
        this.setState({ portalRoot: document.getElementById('portal-root') });
      }
    };

    return (
      <div className={classes} ref={setPortalRootAfterInitialRender}>
        <Tabs
          rootClassName={css.tabsContainer}
          navRootClassName={css.nav}
          tabRootClassName={css.tab}
          linkClassName={css.tabLink}
          selectedLinkClassName={css.selectedTabLink}
          titleClassName={css.tabsTitle}
          title={tabsTitleMessage}
          currentUserHasListings={currentUserHasListings}
          isNewListingFlow={params.type === LISTING_PAGE_PARAM_TYPE_NEW}
          orgId={orgIdFromUrl || currentListing?.attributes?.publicData?.organizationId}
          tabGroups={TAB_GROUPS.map(group => ({
            label: intl.formatMessage({ id: group.labelKey }),
            tabs: group.tabs,
          }))}
          showLogo
        >
          {TABS.map(tab => {
            return (
              <EditListingWizardTab
                {...rest}
                orgIdFromUrl={orgIdFromUrl}
                key={tab}
                tabId={`${id}_${tab}`}
                tabLabel={tabLabel(intl, tab)}
                tabLinkProps={tabLink(tab)}
                selected={selectedTab === tab}
                disabled={isNewListingFlow && !tabsStatus[tab]}
                tab={tab}
                intl={intl}
                params={params}
                listing={listing}
                currentUser={currentUser}
                marketplaceTabs={TABS}
                errors={errors}
                handleCreateFlowTabScrolling={this.handleCreateFlowTabScrolling}
                handlePublishListing={this.handlePublishListing}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            );
          })}
        </Tabs>
      </div>
    );
  }
}

EditListingWizard.defaultProps = {
  className: null,
  currentUser: null,
  rootClassName: null,
  listing: null,
  updateInProgress: false,
};

EditListingWizard.propTypes = {
  id: string.isRequired,
  className: string,
  currentUser: propTypes.currentUser,
  rootClassName: string,
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: string.isRequired,
  }).isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: shape({
    attributes: shape({
      publicData: object,
      description: string,
      geolocation: object,
      pricing: object,
      title: string,
    }),
    images: array,
  }),

  errors: shape({
    createListingDraftError: object,
    updateListingError: object,
    publishListingError: object,
    showListingsError: object,
    uploadImageError: object,
  }).isRequired,

  onManageDisableScrolling: func.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withViewport, injectIntl)(EditListingWizard);
